import { Box, Button } from '@mui/material';
import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon, TrashIcon } from '~/assets/icons';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';
import ContactList from '~/components/contact/ContactList/ContactList';
import EditCustomerDialog from '~/components/customer/edit-customer-dialog/EditCustomerDialog';
import usePermission from '~/hooks/usePermission';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { customerActions } from '~/thunks/customer/customerSlice';
import { handleGetContacts, handleGetDetail } from '~/thunks/customer/customerThunk';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { PERMISSIONS } from '~/utils/constants/permission';
import { catchError, handleApiError, notify } from '~/utils/helper';
import { ICustomer, IPayloadDelete } from '~/utils/interface/customer';
import styles from './Contact.module.scss';
import classNames from 'classnames/bind';
import customerApi from '~/apis/customer';

const cx = classNames.bind(styles);

type ContactProps = {
  id: string;
  customer?: ICustomer;
};

const Contact = (props: ContactProps) => {
  //#region Destructuring Props
  const { id, customer } = props;

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation('admin/customer');
  const dispatch = useAppDispatch();
  const permissions = usePermission();
  //#endregion Declare Hook

  //#region Selector
  const { filter, needRefreshData, openAlertDelete, selectedIds, menuSelectedId } = useAppSelector(
    (state) => state.customer
  );
  //#endregion Selector

  //#region Declare State

  const [editData, setEditData] = useState<ICustomer>();
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [editDialogMode, setEditDialogMode] = useState<string>(DIALOG_MODE_CREATE); // Default to CREATE mode
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    dispatch(customerActions.setFilter({ search: '', searchFields: [], page: 1 }));
  }, [dispatch]);

  useEffect(() => {
    const { search, searchFields, ...contactFilter } = filter;
    dispatch(handleGetContacts({ ...contactFilter, parentId: id }))
      .unwrap()
      .catch((e: any) => {
        handleApiError(e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id, needRefreshData]);
  //#endregion Implement Hook

  //#region Handle Function

  const handleOpen = () => {
    setEditDialogMode(DIALOG_MODE_CREATE);
    setOpenEditDialog(true);
  };

  const handleOpenEditCustomer = async (id: string) => {
    try {
      const response = await dispatch(handleGetDetail(id)).unwrap();
      setEditData(response); // Set the fetched data to editData
      setEditDialogMode(DIALOG_MODE_EDIT); // Set the mode to EDIT
      setOpenEditDialog(true); // Open the edit modal
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleSetOpen = (open: boolean, needRefreshData?: boolean) => {
    setOpenEditDialog(open); // Correctly set the dialog state
    needRefreshData && dispatch(customerActions.changeNeedRefreshData(needRefreshData));
    if (!open) setEditData(undefined); // Reset editData when modal closes
  };

  const openAlertDeleteModal = useCallback(() => {
    dispatch(customerActions.changeOpenAlertDelete(true));
  }, [dispatch]);

  const onDeleteSelected = useCallback(async () => {
    let payloadDeleteCustomers: IPayloadDelete =
      menuSelectedId !== undefined ? { ids: [menuSelectedId] } : { ids: [...selectedIds] };
    try {
      await customerApi.delete(payloadDeleteCustomers);
      notify('success', t('deleteSuccessMessage'));
      dispatch(customerActions.changeNeedRefreshData(true));
      dispatch(customerActions.changeOpenAlertDelete(false));
    } catch (e: unknown) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
  }, [dispatch, menuSelectedId, selectedIds, t]);

  const closeAlertDeleteModal = useCallback(() => {
    dispatch(customerActions.changeMenuSelectId(undefined));
    dispatch(customerActions.changeOpenAlertDelete(false));
  }, [dispatch]);
  //#endregion Handle Function

  return (
    <>
      <Box className='flex'>
        {permissions[PERMISSIONS.contact_view] && (
          <Button
            className={cx(
              { 'visibility: visible': selectedIds.length > 0 },
              { 'visibility: hidden': selectedIds.length <= 0 }
            )}
            onClick={openAlertDeleteModal}
            variant='contained'
            color='error'
            disableElevation
            startIcon={<TrashIcon className='w-[17px] h-[17px]' stroke='white' />}
          >
            {t('delete')}
          </Button>
        )}
        <Button
          className='ml-auto'
          variant='contained'
          disableElevation
          startIcon={<PlusIcon className='w-[10px] h-[10px]' />}
          onClick={handleOpen}
        >
          {t('addNew')}
        </Button>
      </Box>
      <ContactList onOpenEdit={handleOpenEditCustomer} />
      <EditCustomerDialog
        open={openEditDialog}
        mode={editDialogMode}
        setOpen={handleSetOpen}
        data={editData}
        customer={customer}
      />
      <ModalAlertDelete
        open={openAlertDelete}
        description={t('alertDescriptionDeleteContact')}
        onCancel={closeAlertDeleteModal}
        onConfirm={onDeleteSelected}
      />
    </>
  );
};

export default memo(Contact);
